import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, Package, Eye, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('comprovantes'); // 'comprovantes', 'chat' or 'conversas'
  const [comprovantes, setComprovantes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComprovante, setSelectedComprovante] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const adminHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    loadComprovantes();
    loadMessages();
    loadConversations();
    // Auto refresh every 30 seconds
    const interval = setInterval(() => {
      loadComprovantes();
      loadMessages();
      loadConversations();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadComprovantes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes`, { headers: adminHeaders() });
      if (response.status === 401 || response.status === 403) { toast({ title: 'Acesso negado', description: 'Faca login como admin', variant: 'destructive' }); return; }
      const data = await response.json();
      setComprovantes(data);
    } catch (error) {
      console.error('Failed to load comprovantes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support/messages`, { headers: adminHeaders() });
      const data = await response.json();
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/conversations`, { headers: adminHeaders() });
      const data = await response.json();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/conversations/${conversationId}/messages`);
      const data = await response.json();
      setConversationMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation messages:', error);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadConversationMessages(conversation.id);
  };

  const handleReply = async (messageId, email, response) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support/messages/${messageId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response, email })
      });
      
      toast({
        title: '✅ Resposta enviada!',
        description: `Resposta para ${email}`,
      });
      
      // Reload appropriate data
      loadMessages();
      loadConversations();
      if (selectedConversation) {
        loadConversationMessages(selectedConversation.id);
      }
    } catch (error) {
      toast({
        title: '❌ Erro ao enviar',
        description: 'Tente novamente',
        variant: 'destructive'
      });
    }
  };

  const viewComprovante = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes/${id}`, { headers: adminHeaders() });
      const data = await response.json();
      setSelectedComprovante(data);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to load comprovante:', error);
    }
  };

  const processComprovante = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes/${id}/process`, {
        method: 'POST',
        headers: adminHeaders()
      });
      
      toast({
        title: 'Comprovante aprovado!',
        description: 'O comprovante foi marcado como aprovado',
      });
      
      setShowModal(false);
      loadComprovantes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao processar comprovante',
        variant: 'destructive'
      });
    }
  };

  const rejectComprovante = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes/${id}/reject`, {
        method: 'POST',
        headers: adminHeaders()
      });
      
      toast({
        title: 'Comprovante recusado',
        description: 'O comprovante foi marcado como recusado',
      });
      
      setShowModal(false);
      loadComprovantes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao recusar comprovante',
        variant: 'destructive'
      });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === comprovantes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(comprovantes.map(c => c.id));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast({
        title: '⚠️ Nenhum selecionado',
        description: 'Selecione comprovantes para deletar',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm(`Tem certeza que deseja deletar ${selectedIds.length} comprovante(s)?`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes/delete`, {
        method: 'POST',
        headers: adminHeaders(),
        body: JSON.stringify({ ids: selectedIds })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erro ao deletar');
      }
      
      toast({
        title: '✅ Deletado com sucesso!',
        description: `${result.deleted_count} comprovante(s) deletado(s)`,
      });
      
      setSelectedIds([]);
      loadComprovantes();
    } catch (error) {
      toast({
        title: '❌ Erro',
        description: error.message || 'Falha ao deletar comprovantes',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllComprovantes = async () => {
    const confirmMessage = `⚠️ ATENÇÃO! Você está prestes a deletar TODOS os ${comprovantes.length} comprovantes!

Esta ação é IRREVERSÍVEL e irá:
- Deletar todos os comprovantes de pagamento
- Limpar o histórico de vendas
- Não pode ser desfeita

Digite "DELETAR TUDO" para confirmar:`;

    const userInput = prompt(confirmMessage);
    
    if (userInput !== 'DELETAR TUDO') {
      toast({
        title: '✋ Cancelado',
        description: 'Operação cancelada pelo usuário',
      });
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/comprovantes/delete-all`, {
        method: 'POST',
        headers: adminHeaders()
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erro ao deletar todos');
      }
      
      toast({
        title: '✅ Limpeza completa!',
        description: `${result.deleted_count} comprovantes deletados`,
      });
      
      setSelectedIds([]);
      loadComprovantes();
    } catch (error) {
      toast({
        title: '❌ Erro',
        description: error.message || 'Falha ao limpar comprovantes',
        variant: 'destructive'
      });
    } finally {
      setDeleting(false);
    }
  };

  const pendingCount = comprovantes.filter(c => !c.processed).length;
  const processedCount = comprovantes.filter(c => c.processed).length;
  const totalValue = comprovantes.reduce((sum, c) => sum + (c.total || 0), 0);
  const pendingMessages = messages.filter(m => !m.replied).length;

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 gaming-title glow-red">
                PAINEL ADMIN
              </h1>
              <p className="text-xl text-gray-400">
                {activeTab === 'comprovantes' ? 'Gerenciamento de comprovantes' : 
                 activeTab === 'conversas' ? 'Conversas com clientes' : 'Mensagens de suporte'}
              </p>
            </div>
            <div className="flex gap-3">
              {selectedIds.length > 0 && (
                <Button
                  onClick={deleteSelected}
                  disabled={deleting}
                  className="bg-red-700 hover:bg-red-800 text-white gaming-title border-2 border-red-600"
                >
                  {deleting ? '⏳ DELETANDO...' : `🗑️ DELETAR (${selectedIds.length})`}
                </Button>
              )}
              <Button
                onClick={deleteAllComprovantes}
                disabled={deleting}
                className="bg-red-900 hover:bg-red-950 text-white gaming-title border-2 border-red-700"
              >
                {deleting ? '⏳ LIMPANDO...' : '🧹 APAGAR TUDO'}
              </Button>
              <Button
                onClick={loadComprovantes}
                className="bg-red-600 hover:bg-red-700 text-white gaming-title"
              >
                🔄 ATUALIZAR
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setActiveTab('comprovantes')}
              className={`px-6 py-3 rounded-lg font-bold gaming-title transition-all ${
                activeTab === 'comprovantes'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              📦 COMPROVANTES {pendingCount > 0 && `(${pendingCount})`}
            </button>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gray-900 border-2 border-yellow-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Pendentes</p>
                    <p className="text-4xl font-bold text-yellow-500 gaming-title">{pendingCount}</p>
                  </div>
                  <Clock className="w-12 h-12 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-2 border-green-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Processados</p>
                    <p className="text-4xl font-bold text-green-500 gaming-title">{processedCount}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-2 border-red-600/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Valor Total</p>
                    <p className="text-4xl font-bold text-red-600 gaming-title">
                      R$ {totalValue.toFixed(2)}
                    </p>
                  </div>
                  <Package className="w-12 h-12 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Content Based on Active Tab */}
        {activeTab === 'comprovantes' ? (
          /* Comprovantes List */
          <Card className="bg-gray-900 border-2 border-red-900/30">
          <CardHeader className="border-b border-red-900/30">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white gaming-title">COMPROVANTES RECEBIDOS</CardTitle>
              {comprovantes.length > 0 && (
                <label className="flex items-center gap-2 text-gray-400 cursor-pointer hover:text-white transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === comprovantes.length}
                    onChange={toggleSelectAll}
                    className="w-5 h-5 rounded border-red-600 text-red-600 focus:ring-red-600 cursor-pointer"
                  />
                  <span className="text-sm font-semibold">Selecionar Todos</span>
                </label>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-400">Carregando...</p>
              </div>
            ) : comprovantes.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Nenhum comprovante recebido ainda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {comprovantes.map((comp) => (
                  <div
                    key={comp.id}
                    className={`bg-gray-800 rounded-xl p-6 border-2 transition-all hover:border-red-600/50 ${
                      comp.processed ? 'border-green-600/30' : 'border-yellow-600/30'
                    } ${selectedIds.includes(comp.id) ? 'ring-2 ring-red-600' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Checkbox */}
                      <div className="flex items-start gap-4 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(comp.id)}
                          onChange={() => toggleSelect(comp.id)}
                          className="mt-1 w-5 h-5 rounded border-red-600 text-red-600 focus:ring-red-600 cursor-pointer"
                        />
                        <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={comp.status === 'approved' ? 'bg-green-600' : comp.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}>
                            {comp.status === 'approved' ? 'Aprovado' : comp.status === 'rejected' ? 'Recusado' : 'Pendente'}
                          </Badge>
                          <span className="text-gray-500 text-sm">
                            {new Date(comp.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-400 mb-2">Produtos:</p>
                            {comp.cart_items?.map((item, idx) => (
                              <p key={idx} className="text-white">
                                {item.productName} - {item.planType}
                              </p>
                            ))}
                          </div>
                          <div>
                            <p className="text-gray-400 mb-2">Contato:</p>
                            {comp.customer_name && (
                              <p className="text-white text-xs mb-1 font-bold">
                                {comp.customer_name}
                              </p>
                            )}
                            {comp.customer_email && (
                              <p className="text-white text-xs mb-1">
                                {comp.customer_email}
                              </p>
                            )}
                            {comp.customer_discord && (
                              <p className="text-indigo-400 text-xs mb-1">
                                Discord: {comp.customer_discord}
                              </p>
                            )}
                            {comp.delivery_method && (
                              <p className="text-yellow-400 text-xs mb-1 uppercase">
                                Envio: {comp.delivery_method}
                              </p>
                            )}
                            {comp.customer_whatsapp && (
                              <div className="flex items-center gap-2">
                                <p className="text-white text-xs">
                                  {comp.customer_whatsapp}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const phone = comp.customer_whatsapp.replace(/\D/g, '');
                                    window.open(`https://wa.me/${phone}`, '_blank');
                                  }}
                                  className="text-green-500 hover:text-green-400 text-xs underline"
                                >
                                  WhatsApp
                                </button>
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-400 mb-2">Valor Total:</p>
                            <p className="text-2xl font-bold text-red-600 gaming-title">
                              R$ {comp.total?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        </div>
                      </div>

                      <Button
                        onClick={() => viewComprovante(comp.id)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        VER
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        ) : activeTab === 'conversas' ? (
          /* Conversations List */
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Conversations List */}
            <Card className="bg-gray-900 border-2 border-red-900/30">
              <CardHeader className="border-b border-red-900/30">
                <CardTitle className="text-white gaming-title">CONVERSAS</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Carregando...</p>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Nenhuma conversa encontrada</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className={`bg-gray-800 rounded-xl p-4 border-2 transition-all cursor-pointer hover:border-red-600/50 ${
                          selectedConversation?.id === conv.id ? 'border-red-600 ring-2 ring-red-600/30' : 'border-gray-600/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold">{conv.email}</h3>
                          <span className="text-gray-500 text-sm">
                            {new Date(conv.last_message_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm truncate">{conv.last_message?.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <Badge className="bg-blue-600">
                            {conv.message_count} mensagens
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Conversation Messages */}
            <Card className="bg-gray-900 border-2 border-red-900/30">
              <CardHeader className="border-b border-red-900/30">
                <CardTitle className="text-white gaming-title">
                  {selectedConversation ? `CONVERSA COM ${selectedConversation.email}` : 'SELECIONE UMA CONVERSA'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {selectedConversation ? (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {conversationMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-lg ${
                          msg.is_admin 
                            ? 'bg-red-600/20 border border-red-600/30 ml-8' 
                            : 'bg-gray-800 border border-gray-600/30 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-white">
                            {msg.is_admin ? '👨‍💼 Admin' : '👤 Cliente'}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {new Date(msg.created_at).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-gray-300">{msg.message || msg.reply_text}</p>
                        {!msg.replied && !msg.reply_text && !msg.is_admin && (
                          <MessageReplyBox 
                            messageId={msg.id} 
                            email={selectedConversation.email}
                            onReply={(response) => {
                              handleReply(msg.id, selectedConversation.email, response);
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">Selecione uma conversa para ver as mensagens</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Chat/Support Messages */
          <Card className="bg-gray-900 border-2 border-red-900/30">
            <CardHeader className="border-b border-red-900/30">
              <CardTitle className="text-white gaming-title">MENSAGENS DE SUPORTE</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Carregando...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-24 h-24 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">Nenhuma mensagem recebida ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <MessageItem
                      key={msg.id}
                      message={msg}
                      onReply={(response) => {
                        // Handle reply
                        handleReply(msg.id, msg.email, response);
                      }}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedComprovante && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <Card className="bg-gray-900 border-2 border-red-900/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b border-red-900/30">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white gaming-title">DETALHES DO COMPROVANTE</CardTitle>
                <Button
                  onClick={() => setShowModal(false)}
                  variant="ghost"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                {/* Comprovante Image */}
                <div>
                  <p className="text-gray-400 mb-3">Comprovante:</p>
                  <div className="bg-white rounded-lg p-4">
                    <img
                      src={`data:${selectedComprovante.content_type};base64,${selectedComprovante.file_data}`}
                      alt="Comprovante"
                      className="w-full h-auto max-h-[500px] object-contain"
                    />
                  </div>
                </div>

                {/* Details */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-gray-400 mb-2">Produtos Comprados:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20">
                      {selectedComprovante.cart_items?.map((item, idx) => (
                        <div key={idx} className="mb-3 last:mb-0">
                          <p className="text-white font-semibold">{item.productName}</p>
                          <p className="text-gray-400 text-sm">Plano: {item.planType}</p>
                          <p className="text-red-600 font-bold">R$ {item.price?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-400 mb-2">Contato do Cliente:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20 space-y-3">
                      {selectedComprovante.customer_name && (
                        <div>
                          <p className="text-gray-500 text-sm">Nome Completo</p>
                          <p className="text-white font-bold text-lg">
                            {selectedComprovante.customer_name}
                          </p>
                        </div>
                      )}
                      {selectedComprovante.customer_email && (
                        <div>
                          <p className="text-gray-500 text-sm">Email</p>
                          <p className="text-white break-all">
                            {selectedComprovante.customer_email}
                          </p>
                        </div>
                      )}
                      {selectedComprovante.customer_whatsapp && (
                        <div>
                          <p className="text-gray-500 text-sm">WhatsApp</p>
                          <div className="flex items-center gap-3">
                            <p className="text-white">
                              {selectedComprovante.customer_whatsapp}
                            </p>
                            <Button
                              onClick={() => {
                                const phone = selectedComprovante.customer_whatsapp.replace(/\D/g, '');
                                window.open(`https://wa.me/${phone}`, '_blank');
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                            >
                              Abrir WhatsApp
                            </Button>
                          </div>
                        </div>
                      )}
                      {selectedComprovante.customer_discord && (
                        <div>
                          <p className="text-gray-500 text-sm">Discord</p>
                          <p className="text-indigo-400 font-bold">
                            {selectedComprovante.customer_discord}
                          </p>
                        </div>
                      )}
                      {selectedComprovante.delivery_method && (
                        <div>
                          <p className="text-gray-500 text-sm">Enviar produto por</p>
                          <p className="text-yellow-400 font-bold uppercase">
                            {selectedComprovante.delivery_method}
                          </p>
                        </div>
                      )}
                      {selectedComprovante.payment_method && (
                        <div>
                          <p className="text-gray-500 text-sm">Metodo de pagamento</p>
                          <p className="text-green-400 font-bold uppercase">
                            {selectedComprovante.payment_method}
                          </p>
                        </div>
                      )}
                      {!selectedComprovante.customer_email && !selectedComprovante.customer_whatsapp && (
                        <p className="text-gray-500 text-sm italic">
                          Informações de contato não disponíveis
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-400 mb-2">Informações do Pedido:</p>
                    <div className="bg-gray-800 rounded-lg p-4 border border-red-900/20 space-y-3">
                      <div>
                        <p className="text-gray-500 text-sm">Data de Envio</p>
                        <p className="text-white">
                          {new Date(selectedComprovante.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Valor Total</p>
                        <p className="text-3xl font-bold text-red-600 gaming-title">
                          R$ {selectedComprovante.total?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">Status</p>
                        <Badge className={selectedComprovante.processed ? 'bg-green-600' : 'bg-yellow-600'}>
                          {selectedComprovante.processed ? '✓ Processado' : '⏳ Pendente'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {!selectedComprovante.processed && (
                  <div className="flex gap-4">
                    <Button
                      onClick={() => processComprovante(selectedComprovante.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-6 text-lg gaming-title"
                      data-testid="approve-comprovante-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      APROVAR
                    </Button>
                    <Button
                      onClick={() => rejectComprovante(selectedComprovante.id)}
                      className="flex-1 bg-red-700 hover:bg-red-800 text-white font-bold py-6 text-lg gaming-title"
                      data-testid="reject-comprovante-btn"
                    >
                      <X className="w-5 h-5 mr-2" />
                      RECUSAR
                    </Button>
                  </div>
                )}
                {selectedComprovante.processed && (
                  <div className="text-center py-4">
                    <Badge className={selectedComprovante.status === 'approved' ? 'bg-green-600 text-lg px-6 py-2' : 'bg-red-600 text-lg px-6 py-2'}>
                      {selectedComprovante.status === 'approved' ? 'APROVADO' : 'RECUSADO'}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Message Item Component
const MessageItem = ({ message, onReply }) => {
  const [showReplyBox, setShowReplyBox] = React.useState(false);
  const [replyText, setReplyText] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    await onReply(replyText);
    setSending(false);
    setReplyText('');
    setShowReplyBox(false);
  };

  return (
    <div
      className={`bg-gray-800 rounded-xl p-6 border-2 transition-all ${
        message.replied ? 'border-green-600/30' : 'border-yellow-600/30'
      }`}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={message.replied ? 'bg-green-600' : 'bg-yellow-600'}>
              {message.replied ? '✓ Respondido' : '⏳ Pendente'}
            </Badge>
            <span className="text-gray-500 text-sm">
              {new Date(message.created_at).toLocaleString('pt-BR')}
            </span>
          </div>
        </div>

        {/* Message Details */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Nome:</p>
            <p className="text-white font-semibold">{message.name}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Email:</p>
            <p className="text-white">{message.email}</p>
          </div>
        </div>

        {/* Message Content */}
        <div>
          <p className="text-gray-400 text-sm mb-2">Mensagem:</p>
          <div className="bg-gray-900 rounded-lg p-4 border border-red-900/20">
            <p className="text-gray-300 leading-relaxed">{message.message}</p>
          </div>
        </div>

        {/* Reply Box */}
        {!message.replied && (
          <div className="pt-4 border-t border-red-900/20">
            {!showReplyBox ? (
              <Button
                onClick={() => setShowReplyBox(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full"
              >
                💬 RESPONDER
              </Button>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Digite sua resposta aqui..."
                  rows={4}
                  className="w-full bg-gray-900 border border-red-900/30 rounded-lg px-4 py-3 text-white focus:border-red-600 focus:outline-none resize-none"
                />
                <div className="flex gap-3">
                  <Button
                    onClick={handleSendReply}
                    disabled={sending || !replyText.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                  >
                    {sending ? '⏳ ENVIANDO...' : '📧 ENVIAR RESPOSTA'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowReplyBox(false);
                      setReplyText('');
                    }}
                    variant="outline"
                    className="border-red-600 text-white hover:bg-red-600/20"
                  >
                    CANCELAR
                  </Button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  📧 A resposta será enviada para: {message.email}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Show Reply if Already Replied */}
        {message.replied && message.reply_text && (
          <div className="pt-4 border-t border-green-600/20">
            <p className="text-gray-400 text-sm mb-2">Sua resposta:</p>
            <div className="bg-green-600/10 rounded-lg p-4 border border-green-600/30">
              <p className="text-green-100 leading-relaxed">{message.reply_text}</p>
            </div>
            {message.replied_at && (
              <p className="text-gray-500 text-xs mt-2">
                Respondido em: {new Date(message.replied_at).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Message Reply Box Component for Conversations
const MessageReplyBox = ({ messageId, email, onReply }) => {
  const [showReplyBox, setShowReplyBox] = React.useState(false);
  const [replyText, setReplyText] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    await onReply(replyText);
    setSending(false);
    setReplyText('');
    setShowReplyBox(false);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-600/30">
      {!showReplyBox ? (
        <Button
          onClick={() => setShowReplyBox(true)}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          💬 RESPONDER
        </Button>
      ) : (
        <div className="space-y-3">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Digite sua resposta aqui..."
            rows={3}
            className="w-full bg-gray-900 border border-red-900/30 rounded-lg px-3 py-2 text-white text-sm focus:border-red-600 focus:outline-none resize-none"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              {sending ? '⏳' : '📧 ENVIAR'}
            </Button>
            <Button
              onClick={() => {
                setShowReplyBox(false);
                setReplyText('');
              }}
              size="sm"
              variant="outline"
              className="border-red-600 text-white hover:bg-red-600/20"
            >
              ✕
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
